/**
 * Label Sorting Utility
 *
 * Implements label ordering logic from QR-Spec.md.
 * Reverse order for batch pick mode so top of label stack
 * matches top of garment pile.
 *
 * PICKING MODES:
 * - batch_pick: Print all labels reversed (V1 default)
 * - pick_by_line: Print per item, forward order (V1)
 * - hybrid: Reversed with separator sheets (DEFERRED TO PHASE 5)
 *
 * ORDERING AUTHORITY (BLU-502): this module runs NO sort of its own. Label
 * order is DERIVED from `groupPicklist`'s output (`groups.flatMap(garmentIds)`),
 * so the label stack is structurally locked to the picklist sheet — there is
 * exactly one comparator, in `@threadheads/shared`. Do NOT reintroduce a
 * local comparator or widen `SortableGarment` with colour/productType; that
 * would make divergence between sheet and stack expressible again.
 */

import { DEFAULT_PICKLIST_SORT, type PicklistSortConfig } from '@threadheads/shared';
import { groupPicklist, type GroupableGarment } from './picklist-grouping';

/**
 * Supported picking workflow modes.
 *
 * V1 supports: batch_pick, pick_by_line
 * Phase 5 will add: hybrid (requires separator sheet component)
 */
export type PickingMode = 'batch_pick' | 'pick_by_line' | 'hybrid';

/**
 * Minimum interface for the count helpers below. Intentionally minimal —
 * ordering needs `GroupableGarment` (see getLabelsInPrintOrder).
 */
interface SortableGarment {
  id: string;
  sku: string;
  size: string;
  isRedo?: boolean;
  fulfillmentType?: string;
}

/**
 * Get labels in correct print order based on picking mode.
 *
 * From QR-Spec.md:
 * - Batch Pick: REVERSED (top of stack = last picked, aligns with garment pile)
 * - Pick-by-Line: Forward (print per item as picked)
 * - Hybrid: REVERSED with separators (DEFERRED TO PHASE 5)
 *
 * Order is derived from `groupPicklist(config)` — the same grouped output
 * the pick sheet renders — so labels inside a group follow `garmentIds`
 * (stable DB `.order('id')` return order) and groups follow the configured
 * comparator.
 *
 * @param garments - Array of garments
 * @param mode - Picking workflow mode
 * @param config - Picklist sort config (defaults to today's legacy order)
 * @returns Array in correct print order
 *
 * @example
 * // For batch pick mode, labels print in reverse order
 * // so first label printed = bottom of stack = first item to print on
 * const printOrder = getLabelsInPrintOrder(garments, 'batch_pick', config);
 */
export function getLabelsInPrintOrder<T extends GroupableGarment>(
  garments: T[],
  mode: PickingMode,
  config: PicklistSortConfig = DEFAULT_PICKLIST_SORT
): T[] {
  const byId = new Map(garments.map((g) => [g.id, g]));
  const ordered = groupPicklist('label-print-order', garments, config)
    .groups.flatMap((group) => group.garmentIds)
    .map((id) => byId.get(id))
    .filter((g): g is T => g !== undefined);

  // Pick-by-line: forward order (print per line, no reversal needed)
  if (mode === 'pick_by_line') {
    return ordered;
  }

  // Batch Pick (and Hybrid when implemented): reverse order
  // Why reverse? Picker picks top-to-bottom, stacking garments.
  // Last picked = top of stack. Reversed labels align top-to-top.
  return ordered.reverse();
}

/**
 * Calculate total labels for a batch.
 * Includes redos in count.
 *
 * @param garments - Array of garments
 * @returns Total label count
 */
export function getTotalLabelCount(garments: SortableGarment[]): number {
  return garments.length;
}

/**
 * Get redo count from garments.
 *
 * @param garments - Array of garments
 * @returns Number of redo items
 */
export function getRedoCount(garments: SortableGarment[]): number {
  return garments.filter((g) => g.isRedo).length;
}
